/**
 * Stuck-jobs helpers (DEC-315 options A and B).
 *
 * formatAge lives here, NOT in src/lib/timestamp.ts: that module's sweep test asserts every
 * exported formatter renders a 12-hour clock, and an age string ("31h ago") has none.
 */

import { FORCE_CLOSE_ROLES, STUCK_CANCEL_ROLES, SYNC_NUDGE_ROLES } from '../../lib/role-gates';

/**
 * Roles that may force-close a driver's clock — FORCE_CLOSE_ROLES in lib/role-gates.ts, the
 * mirror of FORCE_CLOSE_ROLES in packages/api/src/auth/role-gates.ts (dispatcher-close).
 *
 * Under the DEC-313 union rule the API admits the combined DISPATCH_MANAGER roles
 * (DISPATCH_MANAGER_BILLING_STAFF / _MANAGER), so they are shown the button too; the
 * DISPATCHER_BILLING_* roles are not (quick 261012-jiy — this mirror used to exclude
 * DISPATCH_MANAGER_BILLING_STAFF on purpose, which copied an API defect). UX gate only; the
 * server stays authoritative.
 */
export { FORCE_CLOSE_ROLES, STUCK_CANCEL_ROLES };

export function canForceCloseRole(role: string): boolean {
  return FORCE_CLOSE_ROLES.has(role);
}

/**
 * Roles that may cancel a STUCK in-progress job — STUCK_CANCEL_ROLES in lib/role-gates.ts, the
 * mirror of the api set (DEC-315 option B). UX gate only, the server is authoritative.
 * DISPATCHER_BILLING_STAFF / DISPATCHER_BILLING_MANAGER are not in it.
 */
export function canStuckCancelRole(role: string): boolean {
  return STUCK_CANCEL_ROLES.has(role);
}

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;

/**
 * How long ago `iso` was, relative to `nowMs`: minutes under an hour ("45m ago"), whole hours
 * under 72h ("31h ago"), else days and hours ("3d 8h ago"). '—' for an invalid or future instant.
 */
export function formatAge(iso: string, nowMs: number): string {
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return '—';
  const diff = nowMs - then;
  if (diff < 0) return '—';
  if (diff < HOUR_MS) return `${Math.floor(diff / MINUTE_MS).toString()}m ago`;
  const hours = Math.floor(diff / HOUR_MS);
  if (hours < 72) return `${hours.toString()}h ago`;
  const days = Math.floor(hours / 24);
  return `${days.toString()}d ${(hours % 24).toString()}h ago`;
}

/**
 * DEC-328 — roles that may "Ask driver to sync": SYNC_NUDGE_ROLES in lib/role-gates.ts, the
 * mirror of the API's ASSIGN_JOB gate on POST /v1/jobs/:id/sync-nudge.
 */
export function canSyncNudgeRole(role: string): boolean {
  return SYNC_NUDGE_ROLES.has(role);
}

/**
 * DEC-328 — the line under the "Ask driver to sync" button: null when the job was never
 * nudged, else "Asked 12m ago · synced" once the driver checked in after it, or
 * "Asked 12m ago · waiting" until then.
 */
export function syncNudgeStatusText(
  lastNudgeAt: string | null,
  lastNudgeCheckinAt: string | null,
  nowMs: number,
): string | null {
  if (lastNudgeAt === null) return null;
  const asked = `Asked ${formatAge(lastNudgeAt, nowMs)}`;
  return lastNudgeCheckinAt === null ? `${asked} · waiting` : `${asked} · synced`;
}
